import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

type FooterContent = {
  id: string;
  section_name: string;
  title: string;
  content: string | null;
  link_url: string | null;
  order_position: number;
}

type GroupedContent = {
  [key: string]: FooterContent[];
}

export function Footer() {
  const [footerContent, setFooterContent] = useState<GroupedContent>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFooterContent = async () => {
      const { data, error } = await supabase
        .from('footer_content')
        .select('*')
        .order('order_position');

      if (error) {
        console.error('Error fetching footer content:', error);
        return;
      }

      // Group content by section
      const grouped = (data || []).reduce((acc: GroupedContent, item: FooterContent) => {
        if (!acc[item.section_name]) {
          acc[item.section_name] = [];
        }
        acc[item.section_name].push(item);
        return acc;
      }, {});

      setFooterContent(grouped);
      setLoading(false);
    };

    // Initial fetch
    fetchFooterContent();

    // Subscribe to changes
    const channel = supabase
      .channel('schema-db-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'footer_content'
        },
        () => {
          // Refresh content when changes occur
          fetchFooterContent();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const renderContent = (item: FooterContent) => {
    if (item.link_url) {
      return item.link_url.startsWith('mailto:') ? (
        <a href={item.link_url} className="text-gray-500 hover:text-gray-900">
          {item.content || item.title}
        </a>
      ) : (
        <Link to={item.link_url} className="text-gray-500 hover:text-gray-900">
          {item.title}
        </Link>
      );
    }
    return <p className="text-gray-500">{item.content || item.title}</p>;
  };

  if (loading) {
    return (
      <footer className="bg-gray-100 border-t">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="space-y-3">
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              <div className="h-4 bg-gray-200 rounded w-1/3"></div>
            </div>
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer className="bg-gray-100 border-t">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {Object.entries(footerContent).map(([section, items]) => (
            <div key={section} className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wider">
                {section.charAt(0).toUpperCase() + section.slice(1)}
              </h3>
              <ul className="mt-4 space-y-2">
                {items.map((item) => (
                  <li key={item.id}>
                    {renderContent(item)}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-8 border-t border-gray-200 pt-8">
          <p className="text-center text-gray-400">
            © {new Date().getFullYear()} Sibystock. Tous droits réservés.
          </p>
        </div>
      </div>
    </footer>
  );
}